/**
 * Shopify admin deep-links.
 *
 * `constructShopifyUrl` is a port of the Vue ERP's
 * `RW-ERP-Service/src/utils/shopify-ecom-utils.ts`; the store tables
 * mirror `src/types/constants/shopify-ecom-constant.ts` and must stay
 * identical with them — the `IDS` entries are backend `storeRefId`
 * UUIDs, and the PRODUCTION/DEVELOPMENT values are the Shopify admin
 * store slugs each maps to per environment.
 *
 * Environment selection goes through the shared `isProductionServer()`
 * (`utils/env.ts`), which knows BOTH production API hosts — Vue's
 * `erp.rwdomain.com` and this app's `api.rwdomain.com`. This file used to
 * compare against the Vue host alone, so the production React build
 * resolved the DEVELOPMENT slugs.
 *
 * One deliberate departure from the Vue table: `PRODUCTION.MAIN_US` is
 * `restaurantwareus`, the store's current admin handle, where Vue still
 * carries the legacy numeric `596584-2`.
 */
import { SHOPIFY_ADMIN_URL } from '../constants/env';
import { isProductionServer } from './env';

const SHOPIFY_DEFAULT_URL = 'https://admin.shopify.com/store/';

/** Mirrors Vue's `ENTITY_TYPE` enum (shopify-ecom-constant.ts). */
export const SHOPIFY_ENTITY_TYPE = {
  CUSTOMERS: 'customers',
  ORDERS: 'orders',
  PRODUCTS: 'products',
  DRAFT_ORDERS: 'draft_orders',
} as const;

export type ShopifyEntityType =
  (typeof SHOPIFY_ENTITY_TYPE)[keyof typeof SHOPIFY_ENTITY_TYPE];

/** Mirrors Vue's `SHOPIFY_STORE_IDS` (shopify-ecom-constant.ts). */
export const SHOPIFY_STORE_IDS = {
  PRODUCTION: {
    MAIN_US: 'restaurantwareus',
    MAIN_AE: 'restaurantware-ae',
    PUNCHOUT_US: 'rwpunchout',
    PUNCHOUT_AE: 'aepunchout',
    JETFRESH_US: 'rw-jetfresh',
  },
  DEVELOPMENT: {
    MAIN_US: 'rwdevelopment',
    MAIN_AE: 'rwdevelopment',
    PUNCHOUT_US: 'rwdevelopment',
    PUNCHOUT_AE: 'rwdevelopment',
    JETFRESH_US: 'rw-jetfresh',
  },
  IDS: {
    MAIN_US: '393d1123-183f-413f-b51b-7f4fd75e40b2',
    MAIN_AE: '393d1123-183f-413f-b51b-7f4fd75e40b3',
    PUNCHOUT_US: 'a7e90a25-8a7c-4e8a-b398-7d7c00f0a28c',
    PUNCHOUT_AE: 'f6b4d7bc-5248-4b1b-9f2a-9130b3bfed92',
    JETFRESH_US: 'dab7e0f1-2a04-4445-826b-980d7ae526e6',
  },
} as const;

type StoreKey = keyof typeof SHOPIFY_STORE_IDS.PRODUCTION;

const STORE_KEY_BY_REF_ID: Record<string, StoreKey> = {
  [SHOPIFY_STORE_IDS.IDS.MAIN_US]: 'MAIN_US',
  [SHOPIFY_STORE_IDS.IDS.MAIN_AE]: 'MAIN_AE',
  [SHOPIFY_STORE_IDS.IDS.PUNCHOUT_US]: 'PUNCHOUT_US',
  [SHOPIFY_STORE_IDS.IDS.PUNCHOUT_AE]: 'PUNCHOUT_AE',
  [SHOPIFY_STORE_IDS.IDS.JETFRESH_US]: 'JETFRESH_US',
};

/** `storeRefId` UUID → Shopify store slug for the active environment.
 *  Unknown ids pass through verbatim, same as Vue's `default` branch. */
function resolveStoreSlug(storeRefId: string): string {
  const env = isProductionServer() ? 'PRODUCTION' : 'DEVELOPMENT';
  const key = STORE_KEY_BY_REF_ID[storeRefId];
  return key ? SHOPIFY_STORE_IDS[env][key] : storeRefId;
}

/**
 * Build a Shopify admin URL for an entity, e.g.
 * `https://admin.shopify.com/store/restaurantwareus/orders/<ecomEntityId>`
 * in production, `…/store/rwdevelopment/orders/<ecomEntityId>` elsewhere.
 * Vue signature: `constructShopifyUrl(storeRefId, entityId, entityType)`.
 */
export function constructShopifyUrl(
  storeRefId: string,
  entityId: string,
  entityType: ShopifyEntityType,
): string {
  return `${SHOPIFY_DEFAULT_URL}${resolveStoreSlug(storeRefId)}/${entityType}/${entityId}`;
}

/**
 * Legacy single-argument variant driven by `REACT_APP_SHOPIFY_ADMIN_URL`
 * (defaults to the MAIN_US production orders base). Kept for
 * shippingPrices, whose rows carry no `storeRefId` — everything with a
 * `storeRefId` should use `constructShopifyUrl` above.
 */
export function constructShopifyOrderUrl(ecomEntityId: string): string {
  return `${SHOPIFY_ADMIN_URL.replace(/\/$/, '')}/${ecomEntityId}`;
}
